// src/Pages/Admin/Referrals/referralDialogs.js
import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Delete,
  Search,
  Clear,
  Lock,
  LockOpen,
  Info,
  Business,
  Person,
  Public,
  Edit,
  History,
  Close
} from '@mui/icons-material';
import client from '../../../../../../API';
import { toast } from 'sonner';
import { COMMISSION_TYPES } from './commisiontypeconfig';
import { EditAccessGrantDialog, RateBreakdownDialog } from './accessgrantdialogs';

// =================== ACCESS GRANTS LIST VIEW ===================
export const AccessGrantsListView = ({ open, onClose }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterActive, setFilterActive] = useState('all');
  const [filterScope, setFilterScope] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: accessListData, isLoading } = useQuery(
    ['allAccessGrants', page, rowsPerPage, filterActive, filterScope],
    () => client.commissions.listAccessGrants({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      is_active: filterActive !== 'all' ? filterActive === 'active' : undefined,
      scope_type: filterScope !== 'all' ? filterScope : undefined
    }),
    { enabled: open, keepPreviousData: true }
  );

  const deleteAccessMutation = useMutation(
    (accessId) => client.commissions.deleteAccessGrant(accessId),
    {
      onSuccess: () => {
        toast.success('Access grant deleted successfully');
        queryClient.invalidateQueries('allAccessGrants');
        queryClient.invalidateQueries('referralsAndCommissions');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Failed to delete access grant');
      }
    }
  );

  const filteredGrants = useMemo(() => {
    if (!accessListData?.access_grants) return [];
    
    return accessListData.access_grants.filter((grant) => {
      const matchesSearch = searchTerm === '' || 
        grant.global_user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grant.global_user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grant.global_user?.display_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [accessListData, searchTerm]);

  const handleEdit = (grant) => {
    setSelectedGrant(grant);
    setEditDialogOpen(true);
  };

  const handleViewBreakdown = (grant) => {
    setSelectedGrant(grant);
    setBreakdownDialogOpen(true);
  };

  const handleDelete = (accessId) => {
    if (window.confirm('Are you sure you want to revoke this access grant?')) {
      deleteAccessMutation.mutate(accessId);
    }
  };

  const getActiveOverridesCount = (grant) => {
    return Object.keys(grant.type_specific_rates || {}).length;
  };

  const getEnabledTypesCount = (grant) => {
    if (!grant.enabled_commission_types) return COMMISSION_TYPES.length;
    return grant.enabled_commission_types.length;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <History color="primary" />
              <Typography variant="h6">All Access Grants</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Filters */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'action.disabled' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select 
                    value={filterActive} 
                    onChange={(e) => setFilterActive(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Scope</InputLabel>
                  <Select 
                    value={filterScope} 
                    onChange={(e) => setFilterScope(e.target.value)}
                    label="Scope"
                  >
                    <MenuItem value="all">All Scopes</MenuItem>
                    <MenuItem value="referral">Referral-Specific</MenuItem>
                    <MenuItem value="all">All-Access</MenuItem>
                    <MenuItem value="entity_specific">Entity-Specific</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={() => {
                    setSearchTerm('');
                    setFilterActive('all');
                    setFilterScope('all');
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    {accessListData?.pagination?.total || 0} total grants
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          {isLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Referral/Scope</TableCell>
                      <TableCell>Base Rate</TableCell>
                      <TableCell>Configuration</TableCell>
                      <TableCell>Term</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGrants.map((grant) => {
                      const overridesCount = getActiveOverridesCount(grant);
                      const enabledCount = getEnabledTypesCount(grant);
                      const hasCustomConfig = overridesCount > 0 || enabledCount !== COMMISSION_TYPES.length;

                      return (
                        <TableRow key={grant.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {grant.global_user?.type === 'creator' ? (
                                <Person sx={{ fontSize: 20, color: 'primary.main' }} />
                              ) : (
                                <Business sx={{ fontSize: 20, color: 'secondary.main' }} />
                              )}
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {grant.global_user?.display_name || grant.global_user?.username}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {grant.global_user?.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {grant.scope_type === 'all' ? (
                              <Chip 
                                icon={<Public fontSize="small" />} 
                                label="All Referrals" 
                                size="small" 
                                color="primary"
                              />
                            ) : grant.referral ? (
                              <Box>
                                <Typography variant="body2">
                                  {grant.referral.first_name} {grant.referral.last_name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {grant.referral.email}
                                </Typography>
                              </Box>
                            ) : (
                              <Chip 
                                label={grant.scope_type || 'N/A'} 
                                size="small" 
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {grant.commission_percentage}%
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ({grant.commission_percentage_bps} bps)
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Chip 
                                  label={`${enabledCount}/${COMMISSION_TYPES.length}`} 
                                  size="small" 
                                  color={enabledCount === COMMISSION_TYPES.length ? "success" : "warning"}
                                  variant="outlined"
                                />
                                {hasCustomConfig && (
                                  <Tooltip title="View details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewBreakdown(grant)}
                                    >
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              {overridesCount > 0 && (
                                <Chip 
                                  label={`${overridesCount} override${overridesCount !== 1 ? 's' : ''}`} 
                                  size="small" 
                                  color="warning"
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${grant.term_months}mo`} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {grant.is_active ? (
                              <Chip icon={<LockOpen fontSize="small" />} label="Active" size="small" color="success" />
                            ) : (
                              <Chip icon={<Lock fontSize="small" />} label="Inactive" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {new Date(grant.created_at).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Edit">
                                <IconButton size="small" color="primary" onClick={() => handleEdit(grant)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleDelete(grant.id)}
                                  disabled={deleteAccessMutation.isLoading}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={accessListData?.pagination?.total || 0}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <EditAccessGrantDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedGrant(null);
        }}
        grant={selectedGrant}
        onSuccess={() => {
          queryClient.invalidateQueries('allAccessGrants');
          queryClient.invalidateQueries('referralsAndCommissions');
        }}
      />

      <RateBreakdownDialog
        open={breakdownDialogOpen}
        onClose={() => {
          setBreakdownDialogOpen(false);
          setSelectedGrant(null);
        }}
        accessId={selectedGrant?.id}
        grantInfo={selectedGrant}
      />
    </>
  );
};

// =================== REFERRAL DETAIL DIALOG ===================
export const ReferralDetailDialog = ({ open, onClose, referralId }) => {
  const { data: detailData, isLoading } = useQuery(
    ['referralDetail', referralId],
    () => client.globalUsers.getReferralDetailById(referralId, { include_access: true }),
    { enabled: !!referralId }
  );

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Info color="primary" />
          <Typography variant="h6">Referral Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : detailData ? (
          <Box sx={{ mt: 2 }}>
            {/* Summary Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Referral Value
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(detailData.summary?.referral_value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Potential Commission
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(detailData.summary?.potential_commission)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Invoices
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {detailData.activity?.end_user?.total_invoices || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Status
                    </Typography>
                    <Chip
                      label={detailData.referral?.status}
                      color={detailData.summary?.has_completed ? 'success' : 'warning'}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Access Information */}
            {detailData.referral?.access?.has_commission_rights && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Commission Terms:</strong>{' '}
                  {detailData.referral.access.commission_percentage}% for{' '}
                  {detailData.referral.access.term_months} months
                  {detailData.referral.access.is_all_access && (
                    <Chip 
                      icon={<Public />} 
                      label="All-Access" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Typography>
                {detailData.referral.access.notes && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Notes: {detailData.referral.access.notes}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Activity Details */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Financial Activity
            </Typography>

            {detailData.activity?.end_user?.invoices?.length > 0 ? (
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.activity.end_user.invoices.slice(0, 5).map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.po_number || inv.id}</TableCell>
                        <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{formatCurrency(inv.amount)}</TableCell>
                        <TableCell>
                          <Chip label={inv.status} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                No financial activity yet for this referral
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="error">Failed to load referral details</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};